import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { DataService } from "../../core/core-services/services/dataservices/data.service";
import { LoadingService } from "src/app/core/core-services/services/loader.service";
import { CONTENT_VIEW_LOADER } from "src/app/core";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";

import { CommonModule } from "@angular/common";
import { DownloadBannerComponent } from "src/app/download-banner/download-banner.component";
import { ShortcutsComponent } from "../shortcuts/shortcuts.component";
import { TemplatesComponent } from "../templates/templates.component";

@Component({
    selector: "app-advanced-search-result",
    templateUrl: "./advanced-search-result.component.html",
    styleUrls: ["./advanced-search-result.component.css"],
    imports: [CommonModule, DownloadBannerComponent, ShortcutsComponent, TemplatesComponent]
})
export class AdvancedSearchResultComponent implements OnInit {
  params: any;
  shortcuts: any;
  plates: any;
  downScroll: boolean = true;
  upScroll: boolean = true;
  fromBasicSearch: boolean = true;
  noResults: boolean;
  searchValue: any;
  searchCache: any = {
    type: "",
    params: {},
  };

  constructor(
    private readonly dataService: DataService,
    private readonly sharedData: SharedDataService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly loaderService: LoadingService
  ) {
    let nav = this.router.getCurrentNavigation();

    this.fromBasicSearch = nav && nav?.extras && nav?.extras?.state ? true : false;
    // Since Angular 19 getCurrentNavigation() is null here on a direct load /
    // refresh, so query params must come from the route snapshot instead.
    let queryParams = nav?.initialUrl?.queryParams ?? this.route.snapshot.queryParams;
    this.searchValue = queryParams;

    if (this.fromBasicSearch || queryParams.q) {
      let data = nav?.extras?.state;

      this.searchValue = data?.searchValue || queryParams.q || "";
      this.searchCache = {
        type: "basic",
        params: { q: this.searchValue.shortcut || this.searchValue.q || this.searchValue },
      };
      this.loaderService.start(CONTENT_VIEW_LOADER);
      this.dataService
        .searchTrack({ q: this.searchValue.shortcut || this.searchValue.q || this.searchValue })
        .subscribe({
          next: (res) => {
            this.applyBasicSearchResult(res);
            this.loaderService.stop(CONTENT_VIEW_LOADER);
          },
          error: () => {
            this.loaderService.stop(CONTENT_VIEW_LOADER);
          },
        });
    }
  }
  async ngOnInit() {
    if (!this.fromBasicSearch) {
      const item = this.sharedData.getAttribute("advancedSearchData");
      // `item` can be the literal string "null" (see getResult()'s cleanup
      // call below): JSON.parse("null") does NOT throw, it resolves to the
      // JS value `null`, so an empty/missing check has to happen before the
      // parse rather than relying on the catch block to normalize it.
      try {
        this.params = item ? (JSON.parse(item) ?? {}) : {};
      } catch (e) {
        this.params = {};
      }
      if (this.params.bySearch == "all-flymsg") {
        this.params.bySearch = "";
      }
      if (this.params.createdWithin == "any-time") {
        this.params.createdWithin = "";
      }

      // A direct `?q=` reload has no router `state`, so `fromBasicSearch` is
      // false here even though this is really a basic search — the
      // constructor above already kicked off a `searchTrack()` request for
      // it. When there's no genuine stored advanced-search data (`params` is
      // still empty at this point), skip the redundant getAdvancedSearchResult()
      // fetch below instead of racing it against the constructor's
      // basic-search response. A genuine advanced search always arrives with
      // router state or real stored filter data, so that flow is untouched.
      const trimmedSearchValue = typeof this.searchValue === "string" ? this.searchValue.trim() : "";
      const isDirectQueryReload = !!trimmedSearchValue && Object.keys(this.params).length === 0;

      if (!this.params.bySearch && trimmedSearchValue) {
        this.params.bySearch = trimmedSearchValue;
      } else if (this.params.bySearch && typeof this.params.bySearch !== "string") {
        this.params.bySearch = "";
      }

      if (isDirectQueryReload) {
        return;
      }

      this.getResult(this.params);
    } else if (typeof this.searchValue === "object" && this.searchValue !== null) {
      if (this.searchValue.user_id) {
        this.shortcuts = [this.searchValue];
        return;
      }
      this.plates = [this.searchValue];
    }
  }

  detectUpdates() {
    if (this.searchCache.type == "basic") {
      this.loaderService.start(CONTENT_VIEW_LOADER);
      this.dataService.searchTrack(this.searchCache.params).subscribe({
        next: (res) => {
          this.applyBasicSearchResult(res);
          this.loaderService.stop(CONTENT_VIEW_LOADER);
        },
        error: () => {
          this.loaderService.stop(CONTENT_VIEW_LOADER);
        },
      });
      return;
    }
    if (this.searchCache.type == "advanced") {
      let params = this.searchCache.params;
      this.getResult(params);
      return;
    }
  }

  getResult(params) {
    this.loaderService.start(CONTENT_VIEW_LOADER);

    this.searchCache = {
      type: "advanced",
      params,
    };
    return this.dataService.getAdvancedSearchResult(params).subscribe({
      next: (res) => {
        // `?.` throughout: an empty advanced search can come back without a
        // `result` payload at all, and reading `.data` off it threw — which
        // left the content loader spinning and the no-results state unset.
        const data = res?.result?.data;
        const data2 = res?.result?.data2;

        if (this.params.bySearch == "flyplates") {
          this.shortcuts = [];
        } else {
          this.shortcuts = data && typeof data == "object" ? Object.values(data) : (data ?? []);
        }
        if (this.params.bySearch == "flycuts") {
          this.plates = [];
        } else {
          this.plates = data2 && typeof data2 == "object" ? Object.values(data2) : (data2 ?? []);
        }

        this.updateResultState();

        window.scrollTo(0, 0);
        // Actually clears the key instead of storing a literal `null` — a
        // stored `null` used to survive JSON.parse(item) as the JS value
        // `null` (JSON.parse("null") doesn't throw), crashing the very next
        // `this.params.bySearch` read on a later visit.
        this.sharedData.deleteAttribute("advancedSearchData");
        this.loaderService.stop(CONTENT_VIEW_LOADER);
        this.searchRegex(params);
      },
      error: () => {
        this.loaderService.stop(CONTENT_VIEW_LOADER);
      },
    });
  }

  /**
   * Unpacks a `v1/user/search-data` (basic search) response into
   * `shortcuts`/`plates`. Shared by the constructor's first fetch and
   * `detectUpdates()`'s refetch, which carried byte-identical copies.
   *
   * Tolerates a missing `result`/`data`: a search that matches nothing must
   * still land on the no-results state rather than leave the previous one.
   */
  private applyBasicSearchResult(res: any): void {
    for (const item of res?.result?.data ?? []) {
      if (item.type == "Shortcuts") {
        this.shortcuts = item.items ? item.items : this.shortcuts;
      }
      if (item.type == "FlyPlates") {
        this.plates = item.items ? item.items : this.plates;
      }
    }

    this.updateResultState();
  }

  /**
   * Derives the view flags from whatever `shortcuts`/`plates` now hold:
   * `noResults` drives the "Whoops!" empty state, and the scroll flags are off
   * once both lists are present (each list owns its own infinite scroll).
   */
  private updateResultState(): void {
    if (this.shortcuts && this.plates) {
      this.downScroll = this.upScroll = false;
    }

    this.noResults = !this.shortcuts?.length && !this.plates?.length;
  }

  searchRegex(params) {
    let d = [params.flycut, params.title, params.hasTheWords];
    let m = d.filter((n) => n);
    if (m) {
      this.searchValue = m.join("|");
    }
  }
}
